import * as React from "react";
import { useState } from "react";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { lt } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Lithuanian month names for dropdown
const MONTHS_LT_SHORT = [
  "Sausis", "Vasaris", "Kovas", "Balandis", "Gegužė", "Birželis",
  "Liepa", "Rugpjūtis", "Rugsėjis", "Spalis", "Lapkritis", "Gruodis"
];

// Generate year range (100 years back, 10 years forward)
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear - 100; year <= currentYear + 10; year++) {
    years.push(year);
  }
  return years;
};

const YEARS = generateYears();

function LithuanianDatePicker({
  selected,
  onSelect,
  onCancel,
  onClear,
  className,
  ...props
}) {
  const [tempDate, setTempDate] = useState(selected || new Date());
  const [currentMonth, setCurrentMonth] = useState(selected || new Date());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);

  const handleDaySelect = (day) => {
    if (day) {
      setTempDate(day);
    }
  };

  const handleConfirm = () => {
    onSelect(tempDate);
  };

  const handleClear = () => {
    setTempDate(new Date());
    if (onClear) onClear();
  };

  const handleMonthChange = (monthIndex) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(monthIndex);
    setCurrentMonth(newDate);
    setShowMonthPicker(false);
  };

  const handleYearChange = (year) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(year);
    setCurrentMonth(newDate);
    setShowYearPicker(false);
  };

  // Custom caption component with month/year dropdowns
  const CustomCaption = ({ displayMonth }) => {
    const month = displayMonth.getMonth();
    const year = displayMonth.getFullYear();

    return (
      <div className="flex justify-center items-center gap-1 relative py-1">
        {/* Month dropdown */}
        <Popover open={showMonthPicker} onOpenChange={setShowMonthPicker}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-sm font-medium hover:bg-accent"
            >
              {MONTHS_LT_SHORT[month]}
              <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-40 p-2 bg-slate-900 border-indigo-500/30" align="center">
            <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
              {MONTHS_LT_SHORT.map((monthName, index) => (
                <Button
                  key={monthName}
                  variant={month === index ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-8 text-xs",
                    month === index && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => handleMonthChange(index)}
                >
                  {monthName.slice(0, 3)}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Year dropdown */}
        <Popover open={showYearPicker} onOpenChange={setShowYearPicker}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-sm font-medium hover:bg-accent"
            >
              {year}
              <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-32 p-2 bg-slate-900 border-indigo-500/30" align="center">
            <div className="grid grid-cols-3 gap-1 max-h-48 overflow-y-auto">
              {YEARS.slice().reverse().map((y) => (
                <Button
                  key={y}
                  variant={year === y ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-7 text-xs",
                    year === y && "bg-primary text-primary-foreground"
                  )}
                  onClick={() => handleYearChange(y)}
                >
                  {y}
                </Button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  return (
    <div className={cn("p-3", className)}>
      <DayPicker
        mode="single"
        selected={tempDate}
        onSelect={handleDaySelect}
        month={currentMonth}
        onMonthChange={setCurrentMonth}
        locale={lt}
        showOutsideDays={true}
        classNames={{
          months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
          month: "space-y-4",
          caption: "flex justify-center pt-1 relative items-center",
          caption_label: "hidden",
          nav: "space-x-1 flex items-center",
          nav_button: cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          ),
          nav_button_previous: "absolute left-1",
          nav_button_next: "absolute right-1",
          table: "w-full border-collapse space-y-1",
          head_row: "flex",
          head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
          row: "flex w-full mt-2",
          cell: cn(
            "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50",
            "[&:has([aria-selected])]:rounded-md"
          ),
          day: cn(
            buttonVariants({ variant: "ghost" }),
            "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
          ),
          day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
          day_today: "bg-accent text-accent-foreground",
          day_outside: "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
          day_disabled: "text-muted-foreground opacity-50",
          day_hidden: "invisible",
        }}
        components={{
          Caption: CustomCaption,
          IconLeft: ({ className, ...props }) => (
            <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
          ),
          IconRight: ({ className, ...props }) => (
            <ChevronRight className={cn("h-4 w-4", className)} {...props} />
          ),
        }}
        {...props}
      />
      
      {/* Action buttons */}
      <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-700/50">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="text-muted-foreground hover:text-foreground text-xs"
        >
          Išvalyti
        </Button>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground text-xs"
          >
            Atšaukti
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            className="bg-primary hover:bg-primary/90 text-xs"
          >
            Patvirtinti
          </Button>
        </div>
      </div>
    </div>
  );
}

LithuanianDatePicker.displayName = "LithuanianDatePicker";

export { LithuanianDatePicker };
